import { Fragment, memo, useMemo } from "react"
import { newSetColors } from "@multimediallc/web-utils/colors"
import { ChatEntityType } from "../../../interfaces/chat"
import { mergeClasses } from "../../../utils/css"
import { LogMessage } from "../LogMessage"
import { useMobileBroadcastContext } from "../mobileBroadcastContext"
import { NewMessagesDivider } from "../NewMessagesDivider"
import { RoomNotice } from "../RoomNotice"
import { UserMessage } from "../UserMessage"
import type { ChatEntityData, ReactRoomMessage } from "../../../interfaces/chat"
import type {
    IPrivateMessage,
    IRoomNotice,
} from "@multimediallc/web-utils/types"
import "./ChatEntity.scss"

type IChatEntityProps = {
    entityData: ChatEntityData
    isOldestUnread?: boolean
}

function _ChatEntity({ entityData, isOldestUnread }: IChatEntityProps) {
    const { appPanelEl } = useMobileBroadcastContext()
    const appPanelWrap = useMemo(() => {
        return (
            <div
                ref={(el) => {
                    if (el !== null && appPanelEl.parentElement === null) {
                        el.appendChild(appPanelEl)
                    }
                }}
            />
        )
    }, [appPanelEl])

    function renderMessage() {
        switch (entityData.entityType) {
            case ChatEntityType.RoomMessage:
                return (
                    <UserMessage
                        messageData={entityData as ReactRoomMessage}
                        isPm={false}
                    />
                )
            case ChatEntityType.PmMessage:
                return (
                    <UserMessage
                        messageData={entityData as IPrivateMessage}
                        isPm={true}
                    />
                )
            case ChatEntityType.RoomNotice:
                return <RoomNotice noticeData={entityData as IRoomNotice} />
            case ChatEntityType.LogMessage:
                return <LogMessage message={entityData.message} />
            case ChatEntityType.AppPanel:
                // TODO CBD-2211 implement react AppPanel component
                return appPanelWrap
            default:
                return null
        }
    }

    const isAppPanel = entityData.entityType === ChatEntityType.AppPanel
    const styleOverrides = chatEntityStyleOverrides(entityData)

    return (
        <div
            className="ChatEntity"
            {...(isOldestUnread && {
                "data-testid": "mobile-broadcast-chat-oldest-unread",
            })}
        >
            {isOldestUnread === true && <NewMessagesDivider />}
            <div
                className={mergeClasses(
                    "ChatEntity__contents",
                    isAppPanel ? "ChatEntity__contents--appPanel" : undefined,
                )}
                style={{
                    fontFamily: styleOverrides.fontFamily,
                    fontWeight: styleOverrides.fontWeight,
                }}
                ref={(div) => {
                    if (div) {
                        newSetColors(
                            div,
                            entityData.entityType === ChatEntityType.RoomNotice,
                            styleOverrides.textColor,
                            styleOverrides.backgroundColor,
                        )
                    }
                }}
            >
                <Fragment>{renderMessage()}</Fragment>
            </div>
        </div>
    )
}

type StyleOverrides = {
    fontFamily?: string
    fontWeight?: string
    textColor?: string
    backgroundColor?: string
}

function chatEntityStyleOverrides(entityData: ChatEntityData): StyleOverrides {
    const { entityType } = entityData
    switch (entityType) {
        case ChatEntityType.RoomMessage:
        case ChatEntityType.PmMessage:
            const messageData = entityData as ReactRoomMessage
            return {
                fontFamily: messageData.font,
                textColor: messageData.textColor,
                backgroundColor: messageData.backgroundColor,
            }
        case ChatEntityType.RoomNotice:
            const noticeData = entityData as IRoomNotice
            return {
                textColor: noticeData.foreground,
                backgroundColor: noticeData.background,
                fontWeight: noticeData.weight,
            }
        default:
            return {}
    }
}

export const ChatEntity = memo(_ChatEntity)
